import React, { useState, useEffect } from "react";
import "../styles/components/testimonialCarousel.css";
import { testimonialsContent } from "../content/testimonialsContent";

const TestimonialsCarousel = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [isAutoAdvanceActive, setIsAutoAdvanceActive] = useState(true);
  const [userInteractionTimeout, setUserInteractionTimeout] = useState(null);
  // Track expanded testimonial index (null if none expanded)
  const [expandedIndex, setExpandedIndex] = useState(null);

  // Function to pause auto-advance and resume after delay
  const pauseAutoAdvance = () => {
    setIsAutoAdvanceActive(false);
    
    // Clear existing timeout
    if (userInteractionTimeout) {
      clearTimeout(userInteractionTimeout);
    }
    
    // Resume auto-advance after 15 seconds of no interaction
    const timeout = setTimeout(() => {
      setIsAutoAdvanceActive(true);
    }, 15000);
    
    setUserInteractionTimeout(timeout);
  };

  const handleNext = () => {
    setCurrentTestimonial((prev) => {
      const nextIndex = prev + 1;
      
      if (nextIndex >= testimonialsContent.testimonials.length) {
        // We've reached the duplicate, so reset to 0 after animation completes
        setTimeout(() => {
          setIsTransitioning(false);
          setCurrentTestimonial(0);
          setTimeout(() => {
            setIsTransitioning(true);
          }, 50);
        }, 800);
        return nextIndex;
      }
      
      return nextIndex;
    });
  };
  
  const handlePrev = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonialsContent.testimonials.length) % testimonialsContent.testimonials.length);
  };

  // Handle touch events for mobile swiping
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe || isRightSwipe) {
      pauseAutoAdvance(); // Pause auto-advance on swipe
      
      if (isLeftSwipe) {
        handleNext();
      }
      if (isRightSwipe) {
        handlePrev();
      }
    }
  };

  // Handle mouse drag events
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart(e.clientX);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
  };

  const handleMouseUp = (e) => {
    if (!isDragging) return;
    
    const distance = dragStart - e.clientX;
    const isLeftDrag = distance > 50;
    const isRightDrag = distance < -50;

    if (isLeftDrag || isRightDrag) {
      pauseAutoAdvance(); // Pause auto-advance on drag
      
      if (isLeftDrag) {
        handleNext();
      }
      if (isRightDrag) {
        handlePrev();
      }
    }

    setIsDragging(false);
    setDragStart(0);
  };

  // Handle progress bar click
  const handleProgressClick = (e) => {
    pauseAutoAdvance(); // Pause auto-advance on progress bar click
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progressWidth = rect.width;
    const clickPercentage = clickX / progressWidth;
    const targetTestimonial = Math.floor(clickPercentage * testimonialsContent.testimonials.length);
    
    setCurrentTestimonial(Math.max(0, Math.min(targetTestimonial, testimonialsContent.testimonials.length - 1)));
  };

  // Auto-advance every 5 seconds (only when active)
  useEffect(() => {
    if (!isAutoAdvanceActive) return;
    
    const interval = setInterval(handleNext, 5000);
    return () => clearInterval(interval);
  }, [isAutoAdvanceActive]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (userInteractionTimeout) {
        clearTimeout(userInteractionTimeout);
      }
    };
  }, [userInteractionTimeout]);

  // Helper to determine if a testimonial is long
  const isLong = (text) => text.length > 250;

  // Handler for expanding/collapsing testimonial
  const handleExpandClick = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
    pauseAutoAdvance(); // Pause auto-advance on click
  };

  return (
    <section className="testimonials">
      <h2>Success Stories from Our Students 📈</h2>
      <div className="testimonial-carousel">
        <div 
          className="testimonial-container"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <div className="testimonial-wrapper">
            <div 
              className="testimonial-slider"
              style={{
                transform: `translateX(calc(-${currentTestimonial * 100}% - ${currentTestimonial * 2}rem))`,
                transition: isTransitioning ? 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none'
              }}
            >
              {testimonialsContent.testimonials.map((testimonial, index) => {
                const long = isLong(testimonial.testimonial);
                const expanded = expandedIndex === index;
                return (
                  <div 
                    key={index} 
                    className={`testimonial-card${long ? ' testimonial-card-expandable' : ''}${expanded ? ' expanded' : ''}`}
                    onClick={long ? () => handleExpandClick(index) : undefined}
                    style={long ? { cursor: 'pointer' } : {}}
                  >
                    <div className="testimonial-text-wrapper">
                      <p className="testimonial-text">
                        {long && !expanded
                          ? `${testimonial.testimonial.substring(0, 250)}...`
                          : testimonial.testimonial
                        }
                      </p>
                    </div>
                    <div className="testimonial-author">
                      <h3>- {testimonial.name}</h3>
                    </div>
                  </div>
                );
              })}
              {/* Duplicate first testimonial for seamless loop */}
              {(() => {
                const testimonial = testimonialsContent.testimonials[0];
                const long = isLong(testimonial.testimonial);
                const expanded = expandedIndex === 0;
                return (
                  <div className={`testimonial-card${long ? ' testimonial-card-expandable' : ''}${expanded ? ' expanded' : ''}`}
                    onClick={long ? () => handleExpandClick(0) : undefined}
                    style={long ? { cursor: 'pointer' } : { }}
                  >
                    <div className="testimonial-text-wrapper">
                      <p className="testimonial-text">
                        {long && !expanded
                          ? `${testimonial.testimonial.substring(0, 250)}...`
                          : testimonial.testimonial
                        }
                      </p>
                    </div>
                    <div className="testimonial-author">
                      <h3>- {testimonial.name}</h3>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
        {/* Progress bar */}
        <div className="testimonial-progress-new">
          <div 
            className="progress-line"
            onClick={handleProgressClick}
            style={{ cursor: 'pointer' }}
          >
            <div 
              className="progress-active"
              style={{
                left: `${((currentTestimonial % testimonialsContent.testimonials.length) / (testimonialsContent.testimonials.length - 1)) * (100 - 20)}%`
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
